import { useState, useCallback, useMemo, useRef, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { ArtistDTO, Song } from "../components/view/spotipitan/types";
import { Spotip17anFunctions } from "../utils/api";

export type NavigateArtistTarget =
    | ArtistDTO
    | Song
    | string
    | number
    | { [key: string]: any }
    | null
    | undefined;

export interface UseNavigateToArtistOptions {
    artist?: ArtistDTO | null;
    fallbackName?: string | null;
}

export interface UseNavigateToArtistResult {
    navigateToArtist: (
        target?: NavigateArtistTarget,
        overrideFallbackName?: string | null
    ) => Promise<void>;
    handleNavigateToArtist: (
        target?: NavigateArtistTarget,
        overrideFallbackName?: string | null
    ) => Promise<void>;
    isNavigating: boolean;
}

const isEventObject = (val: unknown): boolean => {
    return Boolean(
        val &&
        typeof val === 'object' &&
        ('nativeEvent' in val || 'preventDefault' in val || (val as any)._isSyntheticEvent)
    );
};

export const useNavigateToArtist = (
    optionsOrArtist?: UseNavigateToArtistOptions | ArtistDTO | null,
    defaultFallbackName?: string | null
): UseNavigateToArtistResult => {
    const navigate = useNavigate();
    const [isNavigating, setIsNavigating] = useState(false);
    const isMounted = useRef(true);

    useEffect(() => {
        isMounted.current = true;
        return () => {
            isMounted.current = false;
        };
    }, []);

    const defaultArtist = useMemo(() => {
        if (!optionsOrArtist) return null;
        if ('artist' in optionsOrArtist && optionsOrArtist.artist !== undefined) {
            return optionsOrArtist.artist ?? null;
        }
        return optionsOrArtist as ArtistDTO;
    }, [optionsOrArtist]);

    const configuredFallbackName = useMemo(() => {
        if (optionsOrArtist && typeof optionsOrArtist === 'object' && 'fallbackName' in optionsOrArtist) {
            return optionsOrArtist.fallbackName ?? defaultFallbackName ?? null;
        }
        return defaultFallbackName ?? null;
    }, [optionsOrArtist, defaultFallbackName]);

    const navigateToArtist = useCallback(async (
        target?: NavigateArtistTarget,
        overrideFallbackName?: string | null
    ) => {
        // If passed directly as an event handler (e.g. onClick={handleNavigateToArtist}),
        // ignore the event object and use the default configured artist/fallbackName.
        const effectiveTarget = isEventObject(target) ? undefined : target;

        if (typeof effectiveTarget === 'number') {
            if (effectiveTarget < 999) {
                navigate(`/artist/${effectiveTarget}`);
                return;
            } else if (effectiveTarget > 999) {
                navigate(`/artist/dz-artist-"${effectiveTarget}`);
            }
        }

        if (typeof effectiveTarget === 'string') {
            const trimmed = effectiveTarget.trim();
            if (trimmed.startsWith("sp-artist-") || trimmed.startsWith("dz-artist-") || trimmed.startsWith("UC")) {
                navigate(`/artist/${trimmed}`);
                return;
            }
            if (trimmed.startsWith("spotify:artist:")) {
                navigate(`/artist/sp-artist-${trimmed.replace("spotify:artist:", "")}`);
                return;
            }
            if (!isNaN(Number(trimmed)) && Number(trimmed) > 0) {
                navigate(`/artist/${trimmed}`);
                return;
            }
            if (trimmed.length === 22 && /^[0-9A-Za-z]{22}$/.test(trimmed)) {
                navigate(`/artist/sp-artist-${trimmed}`);
                return;
            }
        }

        const candidateArtist: any = (
            effectiveTarget && typeof effectiveTarget === 'object'
                ? effectiveTarget
                : defaultArtist
        );

        if (candidateArtist && candidateArtist.id && candidateArtist.type !== 'playlist') {
            const rawId = String(candidateArtist.id).trim();
            if (rawId.startsWith("sp-artist-") || rawId.startsWith("dz-artist-") || rawId.startsWith("UC")) {
                navigate(`/artist/${rawId}`);
                return;
            }
            if (rawId.startsWith("spotify:artist:")) {
                navigate(`/artist/sp-artist-${rawId.replace("spotify:artist:", "")}`);
                return;
            }
            // Bare Deezer artist ID
            if (!isNaN(Number(rawId)) && Number(rawId) > 999) {
                navigate(`/artist/dz-artist-${rawId}`);
                return;
            }
            if (!isNaN(Number(rawId)) && Number(rawId) > 0) {
                navigate(`/artist/${rawId}`);
                return;
            }
            // Bare Spotify artist ID (e.g. 2bKySOuviZxzwrxu7Xq0w is exactly 22 chars)
            if (rawId.length === 22 && /^[0-9A-Za-z]{22}$/.test(rawId)) {
                navigate(`/artist/sp-artist-${rawId}`);
                return;
            }
        }

        if (candidateArtist?.spotip17an_artist && candidateArtist.artist_id) {
            navigate(`/artist/${candidateArtist.artist_id}`);
            return;
        }

        if (candidateArtist?.type === 'artist' && candidateArtist.id) {
            navigate(`/artist/${candidateArtist.id}`);
            return;
        }

        if (candidateArtist?.type === 'album' && candidateArtist.creator_uid) {
            navigate(`/artist/${candidateArtist.creator_uid}`);
            return;
        }

        let artistNameToSearch = "";

        if (typeof effectiveTarget === 'string' && effectiveTarget.trim()) {
            artistNameToSearch = effectiveTarget.trim();
        } else if (overrideFallbackName && overrideFallbackName.trim()) {
            artistNameToSearch = overrideFallbackName.trim();
        } else if (configuredFallbackName && configuredFallbackName.trim()) {
            artistNameToSearch = configuredFallbackName.trim();
        } else if (candidateArtist?.creator_name) {
            artistNameToSearch = candidateArtist.creator_name;
        } else if (candidateArtist?.songs?.[0]?.user?.username) {
            artistNameToSearch = candidateArtist.songs[0].user.username;
        } else if ((candidateArtist as any)?.user?.username) {
            artistNameToSearch = (candidateArtist as any).user.username;
        } else if (Array.isArray((candidateArtist as any)?.artists) && (candidateArtist as any).artists[0]?.name) {
            artistNameToSearch = (candidateArtist as any).artists[0].name;
        } else if (typeof (candidateArtist as any)?.artist === 'string' && (candidateArtist as any).artist) {
            artistNameToSearch = (candidateArtist as any).artist;
        } else if (candidateArtist?.name && candidateArtist.type !== 'album' && candidateArtist.type !== 'playlist') {
            artistNameToSearch = candidateArtist.name;
        } else if (candidateArtist?.username) {
            artistNameToSearch = candidateArtist.username;
        }

        if (!artistNameToSearch) {
            return;
        }

        try {
            if (isMounted.current) {
                setIsNavigating(true);
            }

            const searchRes = await Spotip17anFunctions.use('ytdbSearch').get({
                query: artistNameToSearch,
                searchMode: 'deezer'
            });

            const matched = searchRes?.artists?.find((a: any) =>
                (a.name || a.username)?.toLowerCase() === artistNameToSearch.toLowerCase()
            ) || searchRes?.artists?.[0];

            if (matched?.id) {
                navigate(`/artist/${matched.id}`);
                return;
            }
        } catch (e) {
            console.error("Error navigating to artist", e);
        } finally {
            if (isMounted.current) {
                setIsNavigating(false);
            }
        }

        navigate(`/search?q=${encodeURIComponent(artistNameToSearch)}`);
    }, [defaultArtist, configuredFallbackName, navigate]);

    return {
        navigateToArtist,
        handleNavigateToArtist: navigateToArtist,
        isNavigating
    };
};

export default useNavigateToArtist;
